import React from 'react';

import _ from 'lodash';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, XAxis, YAxis } from 'recharts';
import tailwindConfig from 'stylesheets/tailwind.config';

import { useProjectsAnalyticsCurrentProjectsReportQuery as useCurrentProjectsReportQuery } from 'components/hooks/projectsAnalyticsCurrentProjectsReportQueries';
import { useInfiniteProjectsAnalyticsProjectsReportsCurrentProjectsReportsDashboardReportQuery as useInfiniteCurrentProjectsReportsDashboardReportQuery } from 'components/hooks/projectsAnalyticsProjectsReportsCurrentProjectsReportsDashboardReportQueries';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useProjectsAnalyticsDashboardContext } from 'components/contexts/ProjectsAnalyticsDashboardContext';
import Card from 'components/projectsAnalytics/components/Card';
import CountDisplay from 'components/projectsAnalytics/components/CountDisplay';
import NoDataMessage from 'components/projectsAnalytics/components/NoDataMessage';
import ProjectsModal from 'components/projectsAnalytics/components/ProjectsModal';
import ReportHeader from 'components/projectsAnalytics/components/ReportHeader';
import useFilters from 'components/projectsAnalytics/hooks/useFilters';
import useKeyHighlight from 'components/projectsAnalytics/hooks/useKeyHighlight';
import useProjectsModal from 'components/projectsAnalytics/hooks/useProjectsModal';
import HashedPatternAmber from 'components/projectsAnalytics/patterns/hashed-pattern-amber.svg';
import HashedPatternGreen from 'components/projectsAnalytics/patterns/hashed-pattern-green.svg';

import { pluralizeProjectsName } from 'components/helpers/resources/projects';

const { colors } = tailwindConfig.theme;

const initialBarFills = {
  live: colors.green[400],
  endingInNextSevenDays: 'url(#hashed-pattern-green)',
  future: colors.amber[200],
  startingInNextSevenDays: 'url(#hashed-pattern-amber)',
  endedInLastSevenDays: colors.grey[500],
  noDates: colors.grey[300],
};

const highlightedBarFills = {
  live: { live: colors.green[400], endingInNextSevenDays: colors.green[400] },
  endingInNextSevenDays: { endingInNextSevenDays: colors.green[500] },
  future: {
    future: colors.amber[200],
    startingInNextSevenDays: colors.amber[200],
  },
  startingInNextSevenDays: { startingInNextSevenDays: colors.amber[400] },
  endedInLastSevenDays: { endedInLastSevenDays: colors.grey[500] },
  noDates: { noDates: colors.grey[300] },
};

const barChartWidth = 514;

export const leftBarRadii = [4, 0, 0, 4];
export const middleBarRadii = [0, 0, 0, 0];
export const rightBarRadii = [0, 4, 4, 0];
export const onlyBarRadii = [4, 4, 4, 4];

const sevenDayLifecycleStatusFilter = {
  endedInLastSevenDays: 'ended_and_ended_in_last_seven_days',
  endingInNextSevenDays: 'live_and_ending_in_next_seven_days',
  future: 'future',
  live: 'live',
  noDates: 'no_dates',
  startingInNextSevenDays: 'future_and_starting_in_next_seven_days',
};

export default function CurrentProjectsCard({
  dashboardFilterParams,
  isGeneratingExport,
  onExportClick,
}) {
  const currentActor = useCurrentActor();

  const { filters: cardFilters, handleFilterChange: handleCardFilterChange } =
    useFilters({ includeNoDates: true });

  const projectsAnalyticsDashboardContext =
    useProjectsAnalyticsDashboardContext();

  const {
    domainDashboardConfigurableFields,
    isApprovalsEnabled,
    isDigitalSignaturesEnabled,
    isFoldersEnabled,
    isProjectReviewDatesEnabled,
    isRiskRegisterEnabled,
  } = projectsAnalyticsDashboardContext;

  const { closeProjectsModal, openProjectsModal, projectsModal } =
    useProjectsModal({
      domainDashboardConfigurableFields,
      isApprovalsEnabled,
      isDigitalSignaturesEnabled,
      isFoldersEnabled,
      isProjectReviewDatesEnabled,
      isRiskRegisterEnabled,
    });

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'projectsAnalytics.components.CurrentProjectsCard',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const { data: currentProjectsReport } = useCurrentProjectsReportQuery({
    params: { filter: { ...dashboardFilterParams } },
    queryOptions: { keepPreviousData: true },
  });

  const counts = currentProjectsReport?.data.attributes || {};

  const {
    endedAndEndedInLastSevenDaysCount,
    futureAndStartingInNextSevenDaysCount,
    futureWithoutSubsetCount,
    liveAndEndingInNextSevenDaysCount,
    liveWithoutSubsetCount,
    noStatusCount,
  } = counts || {};

  const totalCount =
    Object.values(counts).reduce((a, b) => a + b, 0) -
    (cardFilters.includeNoDates ? 0 : noStatusCount);

  const barGapTwoPixelEquivalent = totalCount / (barChartWidth / 2);

  const barGapCount = ({ previousBarCounts, remainingBarCounts }) =>
    (
      previousBarCounts.some((count) => count > 0) &&
      remainingBarCounts.some((count) => count > 0)
    ) ?
      barGapTwoPixelEquivalent
    : 0;

  const noStatusBarCount = cardFilters.includeNoDates ? noStatusCount : 0;

  const chartData = {
    live: liveWithoutSubsetCount,
    endingInNextSevenDays: liveAndEndingInNextSevenDaysCount,
    firstBarGap: barGapCount({
      previousBarCounts: [
        liveWithoutSubsetCount,
        liveAndEndingInNextSevenDaysCount,
      ],
      remainingBarCounts: [
        futureWithoutSubsetCount,
        futureAndStartingInNextSevenDaysCount,
        endedAndEndedInLastSevenDaysCount,
        noStatusBarCount,
      ],
    }),
    future: futureWithoutSubsetCount,
    startingInNextSevenDays: futureAndStartingInNextSevenDaysCount,
    secondBarGap: barGapCount({
      previousBarCounts: [
        futureWithoutSubsetCount,
        futureAndStartingInNextSevenDaysCount,
      ],
      remainingBarCounts: [endedAndEndedInLastSevenDaysCount, noStatusBarCount],
    }),
    endedInLastSevenDays: endedAndEndedInLastSevenDaysCount,
    thirdBarGap: barGapCount({
      previousBarCounts: [endedAndEndedInLastSevenDaysCount],
      remainingBarCounts: [noStatusBarCount],
    }),
    noDates: noStatusBarCount,
  };

  const {
    data: domainDashboardReport,
    fetchNextPage: fetchDomainDashboardReportNextPage,
    hasNextPage: hasDomainDashboardReportNextPage,
  } = useInfiniteCurrentProjectsReportsDashboardReportQuery({
    params: {
      filter: projectsModal.filters,
      selections: projectsModal.selections,
    },
    queryOptions: { enabled: projectsModal.isOpen },
  });

  const { pluralRAMSName } = currentActor.division.attributes;

  const handleExportClick = () => {
    onExportClick({ params: { filter: projectsModal.filters } });
  };

  const handleTotalCountClick = () => {
    openProjectsModal({
      filters: {
        ...(dashboardFilterParams.division_ids && {
          company_id: { in: dashboardFilterParams.division_ids },
        }),
        seven_day_lifecycle_status: {
          in: [
            'ended_and_ended_in_last_seven_days',
            'future',
            'live',
            cardFilters.includeNoDates && 'no_dates',
          ].filter(Boolean),
        },
      },
      subtitle: pluralizeProjectsName({
        count: totalCount,
        currentActor,
        shouldIncludeCount: true,
      }),
      title: `${i18nTCommon('projects.currentProjects', { interpolation: { escapeValue: false }, pluralRAMSName })}: ${i18nTCommon('shared.all')}`,
    });

    projectsAnalyticsDashboardContext.sendAnalytics(
      'RAMS analytics: segment click',
      {
        chart: 'Current projects',
        segment: 'All',
      },
    );
  };

  const handleProjectClick = (project) => {
    projectsAnalyticsDashboardContext.sendAnalytics(
      'RAMS analytics: project click',
      {
        chart: 'Current projects',
        project: project.attributes.name,
      },
    );
  };

  return (
    <Card>
      <ReportHeader
        filters={[
          {
            count: noStatusCount,
            label: i18nT('filters.includeNoDates', {
              interpolation: { escapeValue: false },
              pluralRAMSName,
            }),
            name: 'includeNoDates',
            value: cardFilters.includeNoDates,
          },
        ]}
        onFilterChange={handleCardFilterChange}
        onTotalCountClick={totalCount > 0 ? handleTotalCountClick : null}
        title={i18nTCommon('projects.currentProjects', {
          interpolation: { escapeValue: false },
          pluralRAMSName,
        })}
        totalCount={totalCount > 0 ? totalCount : null}
      />
      {currentProjectsReport &&
        (totalCount > 0 ?
          <ReportBody
            cardFilters={cardFilters}
            chartData={chartData}
            dashboardFilterParams={dashboardFilterParams}
            openProjectsModal={openProjectsModal}
          />
        : <NoDataMessage
            message={i18nT('emptyState', {
              interpolation: { escapeValue: false },
              pluralRAMSName,
            })}
          />)}
      {projectsModal.isOpen && (
        <ProjectsModal
          domainDashboardReport={domainDashboardReport}
          hasNextPage={hasDomainDashboardReportNextPage}
          isGeneratingExport={isGeneratingExport}
          onClose={closeProjectsModal}
          onExportClick={handleExportClick}
          onFetchNextPage={fetchDomainDashboardReportNextPage}
          onProjectClick={handleProjectClick}
          selections={projectsModal.selections}
          subtitle={projectsModal.subtitle}
          title={projectsModal.title}
        />
      )}
    </Card>
  );
}

CurrentProjectsCard.propTypes = {
  dashboardFilterParams: PropTypes.shape({
    division_ids: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  isGeneratingExport: PropTypes.bool.isRequired,
};

function ReportBody({
  cardFilters,
  chartData,
  dashboardFilterParams,
  openProjectsModal,
}) {
  const currentActor = useCurrentActor();

  const { handleKeyHighlight, handleKeyUnhighlight, highlightedKey } =
    useKeyHighlight(null);

  const projectsAnalyticsDashboardContext =
    useProjectsAnalyticsDashboardContext();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'projectsAnalytics.components.CurrentProjectsCard',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const { endCountKey, startCountKey } = (() => {
    const nonZeroCountKeys = Object.keys(chartData).filter(
      (countKey) => chartData[countKey] && !countKey.endsWith('Gap'),
    );
    const startCountKey = nonZeroCountKeys[0];
    const endCountKey = nonZeroCountKeys[nonZeroCountKeys.length - 1];

    return { startCountKey, endCountKey };
  })();

  const barFill = (countKey) => {
    if (highlightedKey) {
      return highlightedBarFills[highlightedKey][countKey] || colors.grey[100];
    } else {
      return initialBarFills[countKey];
    }
  };

  const barRadius = (countKey) => {
    if (countKey === startCountKey && countKey === endCountKey) {
      return onlyBarRadii;
    } else if (countKey === startCountKey) {
      return leftBarRadii;
    } else if (countKey === endCountKey) {
      return rightBarRadii;
    } else {
      return middleBarRadii;
    }
  };

  const keyHighlightProps = {
    highlightedKey,
    onKeyHighlight: handleKeyHighlight,
    onKeyUnhighlight: handleKeyUnhighlight,
  };

  const entryMap = {
    endingInNextSevenDays: {
      count: chartData.endingInNextSevenDays,
      key: 'endingInNextSevenDays',
      name: i18nT('entries.endingInNextSevenDays'),
    },
    endedInLastSevenDays: {
      count: chartData.endedInLastSevenDays,
      key: 'endedInLastSevenDays',
      name: i18nT('entries.endedInLastSevenDays'),
    },
    future: {
      count: chartData.future + chartData.startingInNextSevenDays,
      key: 'future',
      name: i18nTCommon('shared.future'),
    },
    live: {
      count: chartData.live + chartData.endingInNextSevenDays,
      key: 'live',
      name: i18nTCommon('shared.live'),
    },
    noDates: {
      count: chartData.noDates,
      key: 'noDates',
      name: i18nTCommon('shared.noDates'),
    },
    startingInNextSevenDays: {
      count: chartData.startingInNextSevenDays,
      key: 'startingInNextSevenDays',
      name: i18nT('entries.startingInNextSevenDays'),
    },
  };

  const handleSegmentClick = ({ entry }) => {
    openProjectsModal({
      filters: {
        seven_day_lifecycle_status: {
          eq: sevenDayLifecycleStatusFilter[entry.key],
        },
        ...(dashboardFilterParams.division_ids && {
          company_id: { in: dashboardFilterParams.division_ids },
        }),
      },
      subtitle: pluralizeProjectsName({
        count: entry.count,
        currentActor,
        shouldIncludeCount: true,
      }),
      title: `${i18nTCommon('projects.currentProjects', { interpolation: { escapeValue: false }, pluralRAMSName: currentActor.division.attributes.pluralRAMSName })}: ${entry.name}`,
    });

    projectsAnalyticsDashboardContext.sendAnalytics(
      'RAMS analytics: segment click',
      {
        chart: 'Current projects',
        segment: entry.name,
      },
    );
  };

  return (
    <div className='tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-pb-6 tw-pl-6 tw-pr-6'>
      <HashedPatternGreen />
      <HashedPatternAmber />
      <div className='tw-mb-4'>
        <BarChart
          barSize={72}
          data={[chartData]}
          height={80}
          layout='vertical'
          margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
          onMouseLeave={handleKeyUnhighlight}
          width={barChartWidth}
        >
          <XAxis domain={[0, 'dataMax']} hide={true} type='number' />
          <YAxis
            dataKey='none'
            domain={[0, 'dataMax']}
            hide={true}
            type='category'
          />
          {Object.keys(chartData).map((countKey) => {
            if (countKey.endsWith('Gap')) {
              return (
                <Bar
                  dataKey={countKey}
                  fill='transparent'
                  key={countKey}
                  stackId='a'
                />
              );
            } else {
              return (
                <Bar
                  cursor='pointer'
                  dataKey={countKey}
                  fill={barFill(countKey)}
                  key={countKey}
                  onClick={() =>
                    handleSegmentClick({ entry: entryMap[countKey] })
                  }
                  onMouseEnter={() => handleKeyHighlight(countKey)}
                  radius={barRadius(countKey)}
                  stackId='a'
                />
              );
            }
          })}
        </BarChart>
      </div>
      <div
        className='tw-flex tw-justify-between'
        onMouseLeave={handleKeyUnhighlight}
      >
        <div className='tw-w-[254px]'>
          <CountDisplay
            countKey='live'
            dotClassName='tw-bg-green-400'
            {...entryMap.live}
            {...keyHighlightProps}
            onClick={() => handleSegmentClick({ entry: entryMap.live })}
          />
          <CountDisplay
            countKey='endingInNextSevenDays'
            dotClassName='tw-bg-green-500'
            {...entryMap.endingInNextSevenDays}
            isSub={true}
            {...keyHighlightProps}
            onClick={() =>
              handleSegmentClick({ entry: entryMap.endingInNextSevenDays })
            }
          />
          <CountDisplay
            countKey='endedInLastSevenDays'
            dotClassName='tw-bg-grey-500'
            {...entryMap.endedInLastSevenDays}
            {...keyHighlightProps}
            onClick={() =>
              handleSegmentClick({ entry: entryMap.endedInLastSevenDays })
            }
          />
        </div>
        <div className='tw-w-[254px]'>
          <CountDisplay
            countKey='future'
            dotClassName='tw-bg-amber-200'
            {...entryMap.future}
            {...keyHighlightProps}
            onClick={() => handleSegmentClick({ entry: entryMap.future })}
          />
          <CountDisplay
            countKey='startingInNextSevenDays'
            dotClassName='tw-bg-amber-400'
            {...entryMap.startingInNextSevenDays}
            isSub={true}
            {...keyHighlightProps}
            onClick={() =>
              handleSegmentClick({ entry: entryMap.startingInNextSevenDays })
            }
          />
          {cardFilters.includeNoDates && (
            <CountDisplay
              countKey='noDates'
              dotClassName='tw-bg-transparent tw-border-2 tw-border-solid tw-border-grey-300'
              {...entryMap.noDates}
              {...keyHighlightProps}
              onClick={() => handleSegmentClick({ entry: entryMap.noDates })}
            />
          )}
        </div>
      </div>
    </div>
  );
}

ReportBody.propTypes = {
  cardFilters: PropTypes.object.isRequired,
  chartData: PropTypes.object.isRequired,
  dashboardFilterParams: PropTypes.shape({
    division_ids: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  openProjectsModal: PropTypes.func.isRequired,
};
